import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Settings2, Copy, Download, CheckCircle,
  Wrench, Calculator, ChevronDown, ChevronUp, Upload, Shield
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHUCK_PRESETS = [
  { label: '6" 3-Jaw Power', size: 6, type: '3-jaw-power', blankW: 2.0, blankH: 1.5, blankL: 3.0 },
  { label: '8" 3-Jaw Power', size: 8, type: '3-jaw-power', blankW: 2.5, blankH: 2.0, blankL: 3.5 },
  { label: '10" 3-Jaw Power', size: 10, type: '3-jaw-power', blankW: 3.0, blankH: 2.5, blankL: 4.0 },
  { label: '12" 3-Jaw Power', size: 12, type: '3-jaw-power', blankW: 3.5, blankH: 3.0, blankL: 5.0 },
  { label: '15" 3-Jaw Power', size: 15, type: '3-jaw-power', blankW: 4.0, blankH: 3.5, blankL: 6.0 },
  { label: '8" 3-Jaw Manual', size: 8, type: '3-jaw-manual', blankW: 2.5, blankH: 2.0, blankL: 3.5 },
  { label: '10" 6-Jaw Power', size: 10, type: '6-jaw-power', blankW: 2.5, blankH: 2.0, blankL: 3.5 },
];

const Field = ({ label, value, onChange, step = 0.001, min, unit = '"', testId }) => (
  <div>
    <label className="block text-[11px] text-industrial-text-secondary mb-1">{label}</label>
    <div className="flex items-center gap-1">
      <input type="number" step={step} min={min} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text font-mono"
        data-testid={testId} />
      {unit && <span className="text-[10px] text-industrial-text-secondary">{unit}</span>}
    </div>
  </div>
);

const SoftJawsPage = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  // Chuck
  const [chuckPreset, setChuckPreset] = useState(1); // index into CHUCK_PRESETS
  const [blankWidth, setBlankWidth] = useState(2.5);
  const [blankHeight, setBlankHeight] = useState(2.0);
  const [blankLength, setBlankLength] = useState(3.5);

  // Grip config
  const [gripMode, setGripMode] = useState('od'); // 'od' = bore jaws for OD grip, 'id' = turn jaws for ID grip
  const [clampDia, setClampDia] = useState(2.0);
  const [gripDepth, setGripDepth] = useState(0.150); // radial pocket depth
  const [gripLength, setGripLength] = useState(0.750); // axial grip length
  const [partLength, setPartLength] = useState(2.0);
  const [wallThickness, setWallThickness] = useState(0.250); // for thin-wall protection
  const [clearance, setClearance] = useState(0.002); // bore oversize for loading

  // Relief
  const [reliefWidth, setReliefWidth] = useState(0.060);
  const [reliefDepth, setReliefDepth] = useState(0.030);

  // Tool
  const [toolNoseRadius, setToolNoseRadius] = useState(0.016);
  const [roughDoc, setRoughDoc] = useState(0.040);
  const [roughFeed, setRoughFeed] = useState(0.006);
  const [finishFeed, setFinishFeed] = useState(0.003);
  const [roughRpm, setRoughRpm] = useState(600);
  const [finishRpm, setFinishRpm] = useState(800);

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Apply chuck preset
  const applyPreset = (idx) => {
    setChuckPreset(idx);
    const p = CHUCK_PRESETS[idx];
    setBlankWidth(p.blankW);
    setBlankHeight(p.blankH);
    setBlankLength(p.blankL);
  };

  // STEP upload for auto-fill
  const handleStepUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API_URL}/api/extract-step-diameter`, form);
      const dia = res.data.max_diameter;
      if (dia > 0) {
        const finalDia = res.data.unit === 'mm' ? dia / 25.4 : dia;
        const rounded = parseFloat(finalDia.toFixed(4));
        // Sanity check: should be reasonable lathe part size
        if (rounded > 0.1 && rounded < 30) {
          setClampDia(rounded);
          setUploadMsg({ type: 'success', text: `Detected diameter: ${rounded}" (${dia.toFixed(2)} ${res.data.unit})` });
        } else {
          setUploadMsg({ type: 'error', text: `Detected ${rounded}" - seems wrong. Check file units.` });
        }
      } else {
        setUploadMsg({ type: 'error', text: 'Could not detect diameter from file' });
      }
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.response?.data?.detail || 'STEP parse failed' });
    }
    setUploading(false);
    e.target.value = '';
    setTimeout(() => setUploadMsg(null), 5000);
  };

  // Calculations
  const calc = useMemo(() => {
    const boreDia = gripMode === 'od'
      ? clampDia + clearance * 2
      : clampDia - clearance * 2;
    const pocketRadialDepth = gripDepth;
    const pocketBottomDia = gripMode === 'od'
      ? boreDia + pocketRadialDepth * 2
      : boreDia - pocketRadialDepth * 2;
    // Floor thickness: axial material remaining = blankLength - gripLength - reliefWidth
    const floorThickness = blankLength - gripLength - reliefWidth;
    const minFloor = 0.236; // 6mm
    const floorOk = floorThickness >= minFloor;
    const thinWallRisk = wallThickness < 0.100;
    const roughPasses = roughDoc > 0 ? Math.ceil(pocketRadialDepth / roughDoc) : 0;
    const chuckSize = CHUCK_PRESETS[chuckPreset]?.size || 8;
    const maxSafeRpm = Math.round(12000 / chuckSize);
    const safePartLength = Math.max(partLength, 0.001);
    const effectiveGripRatio = gripLength / safePartLength;
    const gripRatioOk = effectiveGripRatio >= 0.25;

    return {
      boreDia, pocketRadialDepth, pocketBottomDia, floorThickness,
      floorOk, thinWallRisk, roughPasses, maxSafeRpm, minFloor,
      effectiveGripRatio, gripRatioOk,
    };
  }, [gripMode, clampDia, clearance, gripDepth, blankLength, wallThickness, roughDoc, gripLength, partLength, chuckPreset, reliefWidth]);

  // G-code generation
  const gcode = useMemo(() => {
    const lines = [];
    const c = calc;
    const chuck = CHUCK_PRESETS[chuckPreset];
    const isOD = gripMode === 'od';
    const chuckPressure = c.thinWallRisk ? '4-6' : '8-10';

    lines.push('%');
    lines.push(`O6001 (SOFT JAW BORING - ${isOD ? 'OD GRIP' : 'ID GRIP'})`);
    lines.push(`(CHUCK: ${chuck?.label || 'Custom'})`);
    lines.push(`(CLAMP DIA: ${clampDia.toFixed(4)} | BORE DIA: ${c.boreDia.toFixed(4)})`);
    lines.push(`(GRIP DEPTH: ${gripDepth.toFixed(3)} | GRIP LENGTH: ${gripLength.toFixed(3)})`);
    lines.push(`(CORNER RELIEF: W${reliefWidth.toFixed(3)} D${reliefDepth.toFixed(3)})`);
    lines.push(`(BLANK: ${blankWidth.toFixed(2)} W x ${blankHeight.toFixed(2)} H x ${blankLength.toFixed(2)} L)`);
    if (c.thinWallRisk) lines.push('(WARNING: THIN WALL - REDUCE CHUCK PRESSURE)');
    if (!c.floorOk) lines.push(`(WARNING: FLOOR ${c.floorThickness.toFixed(3)} < MIN ${c.minFloor.toFixed(3)})`);
    if (!c.gripRatioOk) lines.push(`(WARNING: GRIP RATIO ${(c.effectiveGripRatio * 100).toFixed(0)}% < 25%)`);
    lines.push('');

    if (isOD) {
      lines.push('(TOOL LIST:)');
      lines.push('(T08 - BORING BAR FOR JAW BORE)');
      lines.push('');
      lines.push('(SETUP: INSTALL JAW BLANKS WITH BORING RING)');
      lines.push(`(SET CHUCK PRESSURE ${chuckPressure} KG/CM2)`);
      lines.push('(PART ZERO: Z0 = JAW FACE, X0 = CENTERLINE)');
      lines.push('');
      lines.push('G18 G20 G40 G80 G99');
      lines.push(`G50 S${c.maxSafeRpm} (MAX RPM CLAMP)`);
      lines.push('G28 U0 W0');
      lines.push('');

      // Monotonic G71 profile: bore straight wall only (X stays constant)
      const startX = c.boreDia - gripDepth * 2 - 0.2;
      lines.push('(===== ROUGH BORE JAW POCKET =====)');
      lines.push('T0808 (BORING BAR)');
      lines.push(`G97 S${roughRpm} M03`);
      lines.push(`G00 X${startX.toFixed(4)} Z0.100 M08`);
      lines.push('');
      lines.push(`G71 U${roughDoc.toFixed(3)} R0.020`);
      lines.push(`G71 P10 Q20 U-0.010 W0.005 F${roughFeed.toFixed(4)}`);
      lines.push(`N10 G00 X${c.boreDia.toFixed(4)} (BORE DIA)`);
      lines.push(`G01 Z0. F${finishFeed.toFixed(4)} (FACE)`);
      lines.push(`Z-${gripLength.toFixed(4)} (GRIP LENGTH)`);
      lines.push(`N20 X${startX.toFixed(4)} (RETRACT TO START)`);
      lines.push('');

      // G70 finish
      lines.push('(===== FINISH PASS =====)');
      lines.push(`G97 S${finishRpm} M03`);
      lines.push('G70 P10 Q20');
      lines.push('');

      // Separate corner relief groove (not in G71 profile)
      lines.push('(===== CORNER RELIEF GROOVE =====)');
      lines.push(`G00 X${c.boreDia.toFixed(4)} Z-${gripLength.toFixed(4)}`);
      lines.push(`G01 X${(c.boreDia + reliefDepth * 2).toFixed(4)} F${finishFeed.toFixed(4)} (RELIEF DEPTH)`);
      lines.push(`Z-${(gripLength + reliefWidth).toFixed(4)} (RELIEF WIDTH)`);
      lines.push(`G00 X${startX.toFixed(4)}`);
      lines.push('');

      // Face cleanup
      lines.push('(===== FACE CLEANUP =====)');
      lines.push(`G00 X${(c.boreDia + 0.2).toFixed(4)} Z0.050`);
      lines.push(`G01 Z0. F${finishFeed.toFixed(4)} (FACE SKIM)`);
      lines.push(`X${startX.toFixed(4)}`);
      lines.push('G00 Z0.100');

    } else {
      // ID grip - turn OD on jaws
      lines.push('(TOOL LIST:)');
      lines.push('(T01 - OD TURNING TOOL FOR JAW OD)');
      lines.push('');
      lines.push('(SETUP: INSTALL JAW BLANKS WITH EXPANDING MANDREL)');
      lines.push(`(SET CHUCK PRESSURE ${chuckPressure} KG/CM2)`);
      lines.push('(PART ZERO: Z0 = JAW FACE, X0 = CENTERLINE)');
      lines.push('');
      lines.push('G18 G20 G40 G80 G99');
      lines.push(`G50 S${c.maxSafeRpm} (MAX RPM CLAMP)`);
      lines.push('G28 U0 W0');
      lines.push('');

      const clearX = c.boreDia + gripDepth * 2 + 0.2;
      lines.push('(===== ROUGH TURN JAW OD =====)');
      lines.push('T0101 (OD TURNING TOOL)');
      lines.push(`G97 S${roughRpm} M03`);
      lines.push(`G00 X${clearX.toFixed(4)} Z0.100 M08`);
      lines.push('');
      lines.push(`G71 U${roughDoc.toFixed(3)} R0.020`);
      lines.push(`G71 P10 Q20 U0.010 W0.005 F${roughFeed.toFixed(4)}`);
      lines.push(`N10 G00 X${c.boreDia.toFixed(4)} (GRIP DIA)`);
      lines.push(`G01 Z0. F${finishFeed.toFixed(4)}`);
      lines.push(`Z-${gripLength.toFixed(4)} (GRIP LENGTH)`);
      lines.push(`N20 X${clearX.toFixed(4)}`);
      lines.push('');

      // G70 finish
      lines.push('(===== FINISH PASS =====)');
      lines.push(`G97 S${finishRpm} M03`);
      lines.push('G70 P10 Q20');
      lines.push('');

      // Separate corner relief
      lines.push('(===== CORNER RELIEF GROOVE =====)');
      lines.push(`G00 X${c.boreDia.toFixed(4)} Z-${gripLength.toFixed(4)}`);
      lines.push(`G01 X${(c.boreDia - reliefDepth * 2).toFixed(4)} F${finishFeed.toFixed(4)} (RELIEF DEPTH)`);
      lines.push(`Z-${(gripLength + reliefWidth).toFixed(4)} (RELIEF WIDTH)`);
      lines.push(`G00 X${clearX.toFixed(4)}`);
      lines.push('');

      // Face cleanup
      lines.push('(===== FACE CLEANUP =====)');
      lines.push(`G00 X${(c.boreDia - 0.2).toFixed(4)} Z0.050`);
      lines.push(`G01 Z0. F${finishFeed.toFixed(4)} (FACE SKIM)`);
      lines.push(`X${clearX.toFixed(4)}`);
      lines.push('G00 Z0.100');
    }

    lines.push('');
    lines.push('M09');
    lines.push('G28 U0 W0');
    lines.push('M05');
    lines.push('M30');
    lines.push('%');
    return lines.join('\n');
  }, [calc, gripMode, clampDia, gripDepth, gripLength, blankWidth, blankHeight, blankLength, reliefWidth, reliefDepth, roughDoc, roughFeed, finishFeed, roughRpm, finishRpm, chuckPreset]);

  // 2D Jaw Profile Visualizer
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, W, H);

    const margin = 50;
    const isOD = gripMode === 'od';

    // Scale to fit jaw cross-section
    const jawH = blankHeight;
    const jawL = blankLength;
    const maxDim = Math.max(jawH, jawL) || 1;
    const scale = Math.min((W - margin * 2) / jawL, (H - margin * 2) / jawH);

    const mapZ = (z) => margin + z * scale;
    const mapX = (x) => H - margin - x * scale;

    // Jaw blank outline
    ctx.strokeStyle = '#475569';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(mapZ(0), mapX(jawH), jawL * scale, jawH * scale);
    ctx.setLineDash([]);

    // Jaw profile (solid material)
    ctx.fillStyle = '#334155';
    ctx.fillRect(mapZ(0), mapX(jawH), jawL * scale, jawH * scale);

    // Pocket / bore cut
    const boreR = calc.boreDia / 2;
    const pocketDepth = gripDepth;
    ctx.fillStyle = '#1a1a2e'; // cut away material

    if (isOD) {
      // OD grip: bore a pocket from the top of the jaw
      const pocketTop = boreR;
      const pocketBottom = boreR + pocketDepth;
      const pocketStartZ = 0;
      const pocketEndZ = gripLength;

      // Main pocket
      ctx.fillRect(mapZ(pocketStartZ), mapX(pocketBottom), gripLength * scale, pocketDepth * scale);

      // Corner relief
      ctx.fillStyle = '#2d1a1a';
      ctx.fillRect(
        mapZ(gripLength),
        mapX(pocketBottom + reliefDepth),
        reliefWidth * scale,
        (pocketDepth + reliefDepth) * scale
      );

      // Bore surface line (highlight)
      ctx.strokeStyle = '#60a5fa';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(mapZ(0), mapX(boreR));
      ctx.lineTo(mapZ(gripLength), mapX(boreR));
      ctx.lineTo(mapZ(gripLength), mapX(boreR + reliefDepth));
      ctx.lineTo(mapZ(gripLength + reliefWidth), mapX(boreR + reliefDepth));
      ctx.lineTo(mapZ(gripLength + reliefWidth), mapX(pocketBottom));
      ctx.stroke();

      // Dimension labels
      ctx.fillStyle = '#94a3b8';
      ctx.font = '11px monospace';
      ctx.fillText(`BORE ${calc.boreDia.toFixed(3)}"`, mapZ(0) + 4, mapX(boreR) - 6);
      ctx.fillText(`GRIP ${gripLength.toFixed(3)}"`, mapZ(gripLength / 2) - 30, mapX(boreR) - 20);
      ctx.fillText(`DEPTH ${pocketDepth.toFixed(3)}"`, mapZ(gripLength) + reliefWidth * scale + 8, mapX(boreR + pocketDepth / 2));

      // Part outline (dashed)
      ctx.strokeStyle = '#f59e0b44';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(mapZ(0), mapX(clampDia / 2));
      ctx.lineTo(mapZ(partLength), mapX(clampDia / 2));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#f59e0b';
      ctx.font = '10px monospace';
      ctx.fillText(`PART ${clampDia.toFixed(3)}"`, mapZ(0) + 4, mapX(clampDia / 2) + 14);

    } else {
      // ID grip: turned OD pocket
      const pocketOuter = boreR;
      const pocketInner = boreR - pocketDepth;

      ctx.fillRect(mapZ(0), mapX(pocketOuter), gripLength * scale, pocketDepth * scale);

      // Corner relief
      ctx.fillStyle = '#2d1a1a';
      ctx.fillRect(
        mapZ(gripLength),
        mapX(pocketOuter),
        reliefWidth * scale,
        (pocketDepth + reliefDepth) * scale
      );

      ctx.strokeStyle = '#60a5fa';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(mapZ(0), mapX(pocketOuter));
      ctx.lineTo(mapZ(gripLength), mapX(pocketOuter));
      ctx.lineTo(mapZ(gripLength), mapX(pocketOuter - reliefDepth));
      ctx.lineTo(mapZ(gripLength + reliefWidth), mapX(pocketOuter - reliefDepth));
      ctx.stroke();

      ctx.fillStyle = '#94a3b8';
      ctx.font = '11px monospace';
      ctx.fillText(`GRIP DIA ${calc.boreDia.toFixed(3)}"`, mapZ(0) + 4, mapX(pocketOuter) - 6);
    }

    // Corner relief label
    ctx.fillStyle = '#ef4444';
    ctx.font = '9px monospace';
    ctx.fillText('RELIEF', mapZ(gripLength) + 2, mapX(jawH) + 12);

    // Title
    ctx.fillStyle = '#e2e8f0';
    ctx.font = 'bold 12px monospace';
    ctx.fillText(isOD ? 'JAW CROSS-SECTION (OD GRIP)' : 'JAW CROSS-SECTION (ID GRIP)', margin, 20);

  }, [calc, gripMode, clampDia, gripDepth, gripLength, blankHeight, blankLength, reliefWidth, reliefDepth, partLength]);

  const handleCopy = () => {
    navigator.clipboard.writeText(gcode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([gcode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'soft-jaw-bore.nc';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-industrial-bg pb-20" data-testid="soft-jaws-page">
      {/* Header */}
      <div className="sticky top-14 z-20 bg-industrial-surface border-b border-industrial-border px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" /><span className="text-sm">Back</span>
          </button>
          <h2 className="font-heading font-bold text-industrial-safety flex items-center gap-2">
            <Shield className="w-5 h-5" /> SOFT JAW GENERATOR
          </h2>
          <div className="w-12" />
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* 2D Profile Preview */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-industrial-border flex items-center justify-between">
            <span className="text-xs font-medium text-industrial-text">Jaw Profile</span>
            <span className="text-[10px] text-industrial-text-secondary">
              {gripMode === 'od' ? 'OD Grip (Bore Jaws)' : 'ID Grip (Turn OD)'} | Clamp {clampDia.toFixed(3)}" | Grip {gripLength.toFixed(3)}"
            </span>
          </div>
          <canvas ref={canvasRef} width={800} height={280} className="w-full bg-[#1a1a2e]" data-testid="jaw-canvas" />
        </div>

        {/* Chuck Config */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-industrial-text mb-3 flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-industrial-safety" /> Chuck & Jaw Blank
          </h3>
          <div className="mb-3">
            <label className="block text-[11px] text-industrial-text-secondary mb-1">Chuck Preset</label>
            <select
              value={chuckPreset}
              onChange={(e) => applyPreset(parseInt(e.target.value))}
              className="w-full bg-industrial-bg border border-industrial-border rounded px-2 py-1.5 text-sm text-industrial-text"
              data-testid="chuck-preset"
            >
              {CHUCK_PRESETS.map((p, i) => (
                <option key={i} value={i}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Blank Width" value={blankWidth} onChange={setBlankWidth} testId="blank-width" />
            <Field label="Blank Height" value={blankHeight} onChange={setBlankHeight} testId="blank-height" />
            <Field label="Blank Length" value={blankLength} onChange={setBlankLength} testId="blank-length" />
          </div>
        </div>

        {/* Grip Config */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-industrial-text mb-3 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-industrial-safety" /> Clamping Configuration
          </h3>
          {/* Grip Mode Toggle */}
          <div className="flex rounded-lg overflow-hidden border border-industrial-border mb-3">
            <button
              onClick={() => setGripMode('od')}
              data-testid="grip-mode-od"
              className={`flex-1 py-2 text-xs font-medium transition-colors ${gripMode === 'od' ? 'bg-blue-500/20 text-blue-400' : 'bg-industrial-bg text-industrial-text-secondary'}`}
            >
              OD Grip (Bore Jaws)
            </button>
            <button
              onClick={() => setGripMode('id')}
              data-testid="grip-mode-id"
              className={`flex-1 py-2 text-xs font-medium transition-colors ${gripMode === 'id' ? 'bg-blue-500/20 text-blue-400' : 'bg-industrial-bg text-industrial-text-secondary'}`}
            >
              ID Grip (Turn Jaws)
            </button>
          </div>

          {/* STEP Upload */}
          <div className="mb-3 p-2 bg-industrial-bg/60 border border-industrial-border rounded-lg">
            <div className="flex items-center gap-2">
              <label className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1 cursor-pointer transition-colors ${uploading ? 'bg-industrial-surface-highlight text-industrial-text-secondary' : 'bg-industrial-safety/20 border border-industrial-safety/30 text-industrial-safety hover:bg-industrial-safety/30'}`}>
                <Upload className="w-3 h-3" />
                {uploading ? 'Reading...' : 'Upload STEP File'}
                <input type="file" accept=".step,.stp,.STEP,.STP" onChange={handleStepUpload} className="hidden" disabled={uploading} data-testid="step-upload" />
              </label>
              <span className="text-[10px] text-industrial-text-secondary">Auto-detect clamping diameter from STEP/STP file</span>
            </div>
            {uploadMsg && (
              <div className={`mt-2 text-xs px-2 py-1.5 rounded ${uploadMsg.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`} data-testid="upload-feedback">
                {uploadMsg.text}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Clamping Diameter" value={clampDia} onChange={setClampDia} testId="clamp-dia" />
            <Field label="Grip Depth (Radial)" value={gripDepth} onChange={setGripDepth} testId="grip-depth" />
            <Field label="Grip Length (Axial)" value={gripLength} onChange={setGripLength} testId="grip-length" />
            <Field label="Part Length" value={partLength} onChange={setPartLength} testId="part-length" />
            <Field label="Clearance (per side)" value={clearance} onChange={setClearance} testId="clearance" />
            <Field label="Wall Thickness" value={wallThickness} onChange={setWallThickness} testId="wall-thickness" />
            <Field label="Relief Width" value={reliefWidth} onChange={setReliefWidth} testId="relief-width" />
            <Field label="Relief Depth" value={reliefDepth} onChange={setReliefDepth} testId="relief-depth" />
          </div>
        </div>

        {/* Tool & Feeds */}
        <button onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300">
          {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          Tool & Cutting Parameters
        </button>
        {showAdvanced && (
          <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Field label="Tool Nose Radius" value={toolNoseRadius} onChange={setToolNoseRadius} testId="tnr" />
              <Field label="Rough DOC" value={roughDoc} onChange={setRoughDoc} testId="rough-doc" />
              <Field label="Rough Feed (IPR)" value={roughFeed} onChange={setRoughFeed} unit="IPR" testId="rough-feed" />
              <Field label="Finish Feed (IPR)" value={finishFeed} onChange={setFinishFeed} unit="IPR" testId="finish-feed" />
              <Field label="Rough RPM" value={roughRpm} onChange={(v) => setRoughRpm(Math.round(v))} step={50} unit="RPM" testId="rough-rpm" />
              <Field label="Finish RPM" value={finishRpm} onChange={(v) => setFinishRpm(Math.round(v))} step={50} unit="RPM" testId="finish-rpm" />
            </div>
          </div>
        )}

        {/* Calculated Info */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4">
          <h3 className="text-sm font-medium text-industrial-text mb-3 flex items-center gap-2">
            <Calculator className="w-4 h-4 text-industrial-safety" /> Automatic Calculations
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Bore Diameter</span><span className="font-mono text-industrial-text">{calc.boreDia.toFixed(4)}"</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Pocket Radial Depth</span><span className="font-mono text-industrial-text">{calc.pocketRadialDepth.toFixed(4)}"</span></div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Rough Passes</span><span className="font-mono text-industrial-text">{calc.roughPasses}</span></div>
            <div className="flex justify-between">
              <span className="text-industrial-text-secondary">Floor Thickness</span>
              <span className={`font-mono ${calc.floorOk ? 'text-emerald-400' : 'text-red-400'}`}>
                {calc.floorThickness.toFixed(3)}" {calc.floorOk ? '' : '(TOO THIN)'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-industrial-text-secondary">Grip Ratio</span>
              <span className={`font-mono ${calc.gripRatioOk ? 'text-emerald-400' : 'text-yellow-400'}`}>
                {(calc.effectiveGripRatio * 100).toFixed(0)}% {calc.gripRatioOk ? '' : '(< 25%)'}
              </span>
            </div>
            <div className="flex justify-between"><span className="text-industrial-text-secondary">Max Safe RPM</span><span className="font-mono text-industrial-safety">{calc.maxSafeRpm}</span></div>
            {calc.thinWallRisk && (
              <div className="col-span-full p-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400">
                Thin wall part detected ({wallThickness.toFixed(3)}"). Reduce chuck pressure to prevent crushing.
              </div>
            )}
          </div>
        </div>

        {/* G-Code Output */}
        <div className="bg-industrial-surface border border-industrial-border rounded-lg overflow-hidden">
          <div className="px-4 py-2 border-b border-industrial-border flex items-center justify-between">
            <span className="text-sm font-medium text-industrial-text">Generated G-Code (FANUC)</span>
            <div className="flex gap-2">
              <button onClick={handleCopy} className="px-3 py-1 text-xs bg-industrial-surface-highlight rounded flex items-center gap-1 text-industrial-text-secondary hover:text-industrial-text" data-testid="copy-gcode">
                {copied ? <><CheckCircle className="w-3 h-3 text-green-400" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              <button onClick={handleDownload} className="px-3 py-1 text-xs bg-industrial-safety/20 rounded flex items-center gap-1 text-industrial-safety" data-testid="download-gcode">
                <Download className="w-3 h-3" /> .NC
              </button>
            </div>
          </div>
          <pre className="p-4 text-xs font-mono text-industrial-text overflow-x-auto max-h-96 overflow-y-auto leading-relaxed" data-testid="gcode-output">
            {gcode}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default SoftJawsPage;
